import { FastbootError } from "./errors";
import { splitForDownload } from "./sparse";
import type { FastbootTransport } from "./transport";

/** Fastboot responses are ASCII, ≤ 256 bytes (older bootloaders: 64). */
export const MAX_RESPONSE_LEN = 256;
/** How much we hand to one USB transferOut. Chromium splits it into packets. */
export const USB_WRITE_SLICE = 512 * 1024;
/** Used when the device does not report `max-download-size`. */
export const DEFAULT_MAX_DOWNLOAD = 128 * 1024 * 1024;

export type ResponseStatus = "OKAY" | "FAIL" | "DATA" | "INFO";

export interface CommandResult {
  status: "OKAY" | "DATA";
  payload: string;
  info: string[];
}

export interface ProgressEvent {
  /** What is being transferred, e.g. "boot_a" or "download". */
  what: string;
  sent: number;
  total: number;
}

export interface FastbootClientOptions {
  log?: (event: string, fields?: Record<string, unknown>) => void;
  onInfo?: (line: string) => void;
}

/** Vars we read in pre-flight; every field is `undefined` if the device refused the getvar. */
export interface DeviceVars {
  product: string | undefined;
  serialno: string | undefined;
  unlocked: string | undefined;
  currentSlot: string | undefined;
  antiRollback: string | undefined;
  isUserspace: string | undefined;
  maxDownloadSize: number;
  version: string | undefined;
  versionBootloader: string | undefined;
  versionBaseband: string | undefined;
  secure: string | undefined;
  slotCount: string | undefined;
  /** Result of `flashing get_unlock_ability` — undefined when the bootloader lacks it. */
  unlockAbility: boolean | undefined;
}

const enc = new TextEncoder();
const dec = new TextDecoder("utf-8", { fatal: false });

/**
 * Fastboot protocol client over a byte transport. Implements exactly the subset the
 * installer needs: getvar, download, flash, erase, set_active, flashing lock/unlock,
 * flashing get_unlock_ability, reboot targets. `flashing unlock_critical` is deliberately
 * not implemented (QuickHub OS never touches critical/radio partitions from the host).
 */
export class FastbootClient {
  private busy = false;

  constructor(
    readonly transport: FastbootTransport,
    private readonly opts: FastbootClientOptions = {},
  ) {}

  private log(event: string, fields?: Record<string, unknown>): void {
    this.opts.log?.(event, fields);
  }

  /**
   * Send one command and read responses until OKAY/FAIL/DATA. INFO lines are collected
   * and forwarded. Throws FastbootError("fail") on FAIL.
   */
  async command(cmd: string): Promise<CommandResult> {
    if (this.busy) throw new FastbootError("protocol", "fastboot command already in flight", { command: cmd });
    this.busy = true;
    try {
      this.log("fastboot.cmd", { cmd });
      await this.transport.send(enc.encode(cmd));
      const info: string[] = [];
      for (;;) {
        const raw = await this.transport.receive(MAX_RESPONSE_LEN);
        const text = dec.decode(raw);
        if (text.length < 4) throw new FastbootError("protocol", `short fastboot response: ${JSON.stringify(text)}`, { command: cmd });
        const status = text.slice(0, 4);
        const payload = text.slice(4).replace(/\0+$/, "");
        switch (status) {
          case "INFO":
            info.push(payload);
            this.opts.onInfo?.(payload);
            this.log("fastboot.info", { cmd, line: payload });
            continue;
          case "OKAY":
            this.log("fastboot.okay", { cmd, payload });
            return { status: "OKAY", payload, info };
          case "DATA":
            return { status: "DATA", payload, info };
          case "FAIL":
            this.log("fastboot.fail", { cmd, reason: payload });
            throw new FastbootError("fail", `${cmd}: ${payload}`, { command: cmd, reason: payload });
          case "TEXT": // fastbootd may emit TEXT for long INFO output; treat as INFO
            info.push(payload);
            continue;
          default:
            throw new FastbootError("protocol", `unknown fastboot status ${JSON.stringify(status)}`, { command: cmd });
        }
      }
    } finally {
      this.busy = false;
    }
  }

  async getvar(name: string): Promise<string> {
    const r = await this.command(`getvar:${name}`);
    if (r.status !== "OKAY") throw new FastbootError("protocol", `getvar:${name} returned DATA`, { command: `getvar:${name}` });
    return r.payload;
  }

  /** getvar that returns undefined instead of throwing when the bootloader lacks the var. */
  async tryGetvar(name: string): Promise<string | undefined> {
    try {
      return await this.getvar(name);
    } catch (e) {
      if (e instanceof FastbootError && e.code === "fail") return undefined;
      throw e;
    }
  }

  /** `getvar:all` → map. Lines look like `name:value` (slot vars: `has-slot:boot:yes`). */
  async getvarAll(): Promise<Record<string, string>> {
    const r = await this.command("getvar:all");
    const out: Record<string, string> = {};
    for (const line of r.info) {
      const i = line.lastIndexOf(":");
      if (i <= 0) continue;
      out[line.slice(0, i).trim()] = line.slice(i + 1).trim();
    }
    return out;
  }

  async getMaxDownloadSize(): Promise<number> {
    const v = await this.tryGetvar("max-download-size");
    if (!v) return DEFAULT_MAX_DOWNLOAD;
    const n = v.startsWith("0x") || v.startsWith("0X") ? parseInt(v, 16) : parseInt(v, 10);
    if (!Number.isFinite(n) || n <= 0) throw new FastbootError("protocol", `bad max-download-size ${v}`);
    return n;
  }

  /** `flashing get_unlock_ability` → true/false, undefined if unsupported (older bootloaders). */
  async getUnlockAbility(): Promise<boolean | undefined> {
    try {
      const r = await this.command("flashing get_unlock_ability");
      const text = [r.payload, ...r.info].join(" ");
      const m = /get_unlock_ability:\s*(\d)/i.exec(text) ?? /\b([01])\b/.exec(text);
      if (!m) return undefined;
      return m[1] === "1";
    } catch (e) {
      if (e instanceof FastbootError && e.code === "fail") return undefined;
      throw e;
    }
  }

  async readDeviceVars(): Promise<DeviceVars> {
    const antiRollback =
      (await this.tryGetvar("anti-rollback")) ??
      (await this.tryGetvar("rollback")) ??
      (await this.tryGetvar("anti_rollback_version"));
    return {
      product: await this.tryGetvar("product"),
      serialno: await this.tryGetvar("serialno"),
      unlocked: await this.tryGetvar("unlocked"),
      currentSlot: await this.tryGetvar("current-slot"),
      antiRollback,
      isUserspace: await this.tryGetvar("is-userspace"),
      maxDownloadSize: await this.getMaxDownloadSize(),
      version: await this.tryGetvar("version"),
      versionBootloader: await this.tryGetvar("version-bootloader"),
      versionBaseband: await this.tryGetvar("version-baseband"),
      secure: await this.tryGetvar("secure"),
      slotCount: await this.tryGetvar("slot-count"),
      unlockAbility: await this.getUnlockAbility(),
    };
  }

  /** `download:` one buffer that already fits max-download-size. */
  async download(data: Uint8Array, onProgress?: (p: ProgressEvent) => void, what = "download"): Promise<void> {
    const hex = data.byteLength.toString(16).padStart(8, "0");
    const r = await this.command(`download:${hex}`);
    if (r.status !== "DATA") throw new FastbootError("protocol", "download: expected DATA", { command: "download" });
    const accepted = parseInt(r.payload, 16);
    if (accepted !== data.byteLength) {
      throw new FastbootError("protocol", `device accepted ${accepted} bytes, wanted ${data.byteLength}`, { command: "download" });
    }
    for (let off = 0; off < data.byteLength; off += USB_WRITE_SLICE) {
      const slice = data.subarray(off, Math.min(data.byteLength, off + USB_WRITE_SLICE));
      await this.transport.send(slice);
      onProgress?.({ what, sent: off + slice.byteLength, total: data.byteLength });
    }
    // The final OKAY for the data phase.
    const raw = await this.transport.receive(MAX_RESPONSE_LEN);
    const text = dec.decode(raw);
    if (text.startsWith("OKAY")) return;
    if (text.startsWith("FAIL")) throw new FastbootError("fail", `download: ${text.slice(4)}`, { command: "download", reason: text.slice(4) });
    throw new FastbootError("protocol", `unexpected post-download response ${JSON.stringify(text)}`, { command: "download" });
  }

  /**
   * Flash `partition` with `image`, splitting into sparse pieces if the image exceeds
   * max-download-size. Progress is reported over the whole image.
   */
  async flash(
    partition: string,
    image: Uint8Array,
    onProgress?: (p: ProgressEvent) => void,
    maxDownload?: number,
  ): Promise<void> {
    const max = maxDownload ?? (await this.getMaxDownloadSize());
    const pieces = splitForDownload(image, max);
    const total = pieces.reduce((n, p) => n + p.byteLength, 0);
    let done = 0;
    this.log("fastboot.flash.begin", { partition, bytes: image.byteLength, pieces: pieces.length });
    for (const piece of pieces) {
      await this.download(piece, (p) => onProgress?.({ what: partition, sent: done + p.sent, total }), partition);
      done += piece.byteLength;
      await this.command(`flash:${partition}`);
    }
    this.log("fastboot.flash.done", { partition });
  }

  async erase(partition: string): Promise<void> {
    await this.command(`erase:${partition}`);
  }

  async setActive(slot: "a" | "b"): Promise<void> {
    await this.command(`set_active:${slot}`);
  }

  /** `flashing unlock` — the phone shows its own confirmation screen; the USB link drops after. */
  async flashingUnlock(): Promise<void> {
    await this.command("flashing unlock");
  }

  async flashingLock(): Promise<void> {
    await this.command("flashing lock");
  }

  /**
   * reboot targets: undefined → normal boot; "bootloader" → back to fastboot;
   * "fastboot" → userspace fastbootd; "recovery".
   * The device disconnects right after OKAY, which is expected.
   */
  async reboot(target?: "bootloader" | "fastboot" | "recovery"): Promise<void> {
    const cmd = target ? `reboot-${target}` : "reboot";
    await this.command(cmd);
  }
}
